<template>
  <div
    ref="board"
    class="ws-board"
    :class="[`ws-board--${layoutMode}`, `ws-board--${cardMode}`, { 'ws-board--measuring': measuring }]"
    :style="boardStyle"
  >
    <!-- The public schedule board, drawn from the Design tab's two templates:
         the heading once at the top of each day, then every event — all of them
         inside one shared card, or each in a card of its own on a grid of
         columns. Used by the live display (Display.vue), the Dashboard's
         embedded preview and the admin page's per-screen preview, so all three
         draw the very same board.

         Sizes are design pixels (a 1920-wide screen). Display.vue already scales
         an artboard's content to its screen; a preview passes `width` (the
         artboard in design pixels) and `scale` to fit its pane. -->
    <div class="ws-board__days" :style="daysStyle">
      <section
        v-for="(block, index) in dayBlocks"
        :key="block.key"
        class="ws-board__day"
        :class="{ 'ws-board__day--today': block.isToday }"
        :style="dayStyle(index)"
      >
        <div class="ws-board__day-inner" :style="dayInnerStyle">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-if="block.headingHtml" class="ws-board__heading ws-rich" :style="headingStyle" v-html="block.headingHtml"></div>

        <template v-if="block.events.length">
          <div v-if="cardMode === 'single'" class="ws-board__card ws-board__card--single" :style="singleCardStyle">
            <div class="ws-board__rows" :style="rowsStyle(block.events.length)">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div
                v-for="(event, index) in block.events"
                :key="event.key"
                class="ws-board__row ws-rich"
                :class="eventClasses(event)"
                :style="rowStyle(event, index, block.events.length)"
                v-html="event.html"
              ></div>
            </div>
          </div>

          <div v-else class="ws-board__cards" :style="cardsStyle">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              v-for="(event, index) in block.events"
              :key="event.key"
              class="ws-board__card ws-rich"
              :class="eventClasses(event)"
              :style="cardStyle(event, index, block.events.length)"
              v-html="event.html"
            ></div>
          </div>
        </template>

        <div v-else class="ws-board__empty">No scheduled times</div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { getCenteredCardPlacement, getCenteredCardTemplateColumns } from '@/utils/column_layout';
import {
  buildEventTemplateValues,
  buildHeadingTemplateValues,
  computeNowNext,
  isTransparentScheduleColor,
  normalizeWeeklyScheduleDesign,
  renderWeeklyScheduleTemplate,
  scheduleColorWithAlpha,
} from '@/utils/weekly_schedule_N';
import '@/assets/style/weekly_schedule_N_content.css';

const CARD_RADIUS = 12;
const SINGLE_CARD_RADIUS = 14;
// The furthest a day column is shrunk to fit its widest word. Past this,
// words break instead, so one stray long word cannot shrink a whole board.
const MIN_FIT_ZOOM = 0.35;

export default {
  name: 'Weekly_Schedule_Render_N',
  props: {
    // [{ day: ScheduleDay, occurrences: ScheduleOccurrence[], notes: string[] }]
    columns: {
      type: Array,
      default: () => [],
    },
    design: {
      type: Object,
      default: null,
    },
    headerPreset: {
      type: String,
      default: 'standard',
    },
    layout: {
      type: String,
      default: 'day',
    },
    showNowNext: {
      type: Boolean,
      default: true,
    },
    showCountdown: {
      type: Boolean,
      default: false,
    },
    // Minutes past midnight right now, in the board's own timezone.
    nowMinutes: {
      type: Number,
      default: null,
    },
    // A horizontal (marquee) artboard: everything runs on one line instead.
    horizontal: {
      type: Boolean,
      default: false,
    },
    // The board's width in design pixels; unset fills its container.
    width: {
      type: Number,
      default: null,
    },
    // How many CSS pixels one design pixel is (a preview pane's fit).
    scale: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      // How far each day column is shrunk so its widest word fits — a 44px
      // heading in a seventh of the screen, say. 1 = as authored.
      fitZoom: 1,
      measuring: false,
    };
  },
  computed: {
    d() {
      return normalizeWeeklyScheduleDesign(this.design);
    },
    cardMode() {
      return this.d.cardMode === 'single' ? 'single' : 'separate';
    },
    layoutMode() {
      if (this.horizontal) return 'ticker';
      return this.layout === 'week' ? 'week' : 'day';
    },
    // The week layout stacks each day's events, and a ticker runs them in a
    // row, so card columns only shape the single-day board.
    columnCount() {
      return this.layoutMode === 'day' ? Math.max(1, this.d.columns) : 1;
    },
    // Days to a row of the week layout, never more than there are days. They
    // run Sunday first along each row, and a short last row sits centred.
    weekColumnCount() {
      return Math.max(1, Math.min(this.d.weekColumns, this.dayBlocks.length));
    },
    dayGap() {
      return Math.max(16, this.spacing);
    },
    spacing() {
      return this.d.cardSpacing;
    },
    highlight() {
      return isTransparentScheduleColor(this.d.highlightColor) ? '' : this.d.highlightColor;
    },
    dayBlocks() {
      const minutes = Number.isFinite(this.nowMinutes) ? this.nowMinutes : null;
      return (this.columns || []).filter((column) => column && column.day).map((column) => {
        const day = column.day;
        const occurrences = Array.isArray(column.occurrences) ? column.occurrences : [];
        const live = day.isToday && minutes !== null ? computeNowNext(occurrences, minutes) : null;
        const headingValues = buildHeadingTemplateValues(day, {
          headerPreset: this.headerPreset,
          notes: Array.isArray(column.notes) ? column.notes : [],
          nowNext: live,
          showNowNext: this.showNowNext,
          showCountdown: this.showCountdown,
        });
        const events = occurrences.map((occurrence) => {
          const isNow = Boolean(this.showNowNext && live?.current && live.current.key === occurrence.key);
          const isNext = Boolean(this.showNowNext && !isNow && live?.next && live.next.key === occurrence.key);
          const isPast = Boolean(
            day.isToday && minutes !== null && occurrence.endMinutes !== null && occurrence.endMinutes <= minutes && !isNow
          );
          const values = {
            ...headingValues,
            ...buildEventTemplateValues(occurrence, isNow ? 'Now' : isNext ? 'Next' : ''),
          };
          return {
            key: occurrence.key,
            html: renderWeeklyScheduleTemplate(this.d.eventHtml, values),
            isNow,
            isNext,
            isPast,
          };
        });
        return {
          key: day.iso,
          isToday: Boolean(day.isToday),
          headingHtml: renderWeeklyScheduleTemplate(this.d.headingHtml, headingValues),
          events,
        };
      });
    },
    boardStyle() {
      const style = {
        background: this.d.backgroundColor,
        width: Number.isFinite(this.width) && this.width > 0 ? `${this.width}px` : undefined,
      };
      if (Number.isFinite(this.scale) && this.scale > 0 && this.scale !== 1) style.zoom = this.scale;
      return style;
    },
    daysStyle() {
      if (this.layoutMode !== 'week') return {};
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${getCenteredCardTemplateColumns(this.weekColumnCount)}, minmax(0, 1fr))`,
        columnGap: `${this.dayGap}px`,
        // Rows sit further apart than the days in a row, so each row's
        // headings read as the start of a new line of the week.
        rowGap: `${this.dayGap * 2}px`,
      };
    },
    dayInnerStyle() {
      return !this.measuring && this.fitZoom < 1 ? { zoom: this.fitZoom } : {};
    },
    // Everything the fit depends on; a change re-measures.
    fitSignature() {
      return JSON.stringify([
        this.layoutMode,
        this.cardMode,
        this.columnCount,
        this.weekColumnCount,
        this.d.cardPadding,
        this.d.cardSpacing,
        this.width,
        this.dayBlocks.map((block) => [block.headingHtml, block.events.map((event) => event.html)]),
      ]);
    },
    headingStyle() {
      return this.layoutMode === 'ticker' ? {} : { marginBottom: `${Math.max(12, this.spacing)}px` };
    },
    cardBase() {
      return {
        background: this.d.cardBackgroundColor,
        border: `1px solid ${this.d.cardBorderColor}`,
        padding: this.d.cardPadding,
        boxSizing: 'border-box',
      };
    },
    singleCardStyle() {
      return { ...this.cardBase, borderRadius: `${SINGLE_CARD_RADIUS}px` };
    },
    cardsStyle() {
      if (this.layoutMode === 'ticker') return { gap: `${this.spacing}px` };
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${getCenteredCardTemplateColumns(this.columnCount)}, minmax(0, 1fr))`,
        gap: `${this.spacing}px`,
      };
    },
  },
  watch: {
    fitSignature: {
      flush: 'post',
      handler() {
        this.scheduleFit();
      },
    },
  },
  mounted() {
    // Only a change of WIDTH re-fits: the fit itself changes the board's
    // height, and reacting to that would measure forever.
    if (typeof ResizeObserver !== 'undefined') {
      this._fitObserver = new ResizeObserver((entries) => {
        const width = Math.round(entries[0]?.contentRect?.width || 0);
        if (width === this._fitWidth) return;
        this._fitWidth = width;
        this.scheduleFit();
      });
      if (this.$refs.board) this._fitObserver.observe(this.$refs.board);
    }
    // Web fonts change how wide every word is, and screens load theirs late.
    if (typeof document !== 'undefined' && document.fonts) {
      this._onFontsLoaded = () => this.scheduleFit();
      document.fonts.addEventListener?.('loadingdone', this._onFontsLoaded);
      document.fonts.ready?.then(this._onFontsLoaded).catch(() => {});
    }
    this.scheduleFit();
  },
  beforeUnmount() {
    if (this._fitObserver) this._fitObserver.disconnect();
    if (this._fitFrame) cancelAnimationFrame(this._fitFrame);
    if (this._onFontsLoaded && typeof document !== 'undefined' && document.fonts) {
      document.fonts.removeEventListener?.('loadingdone', this._onFontsLoaded);
    }
    this._unmounted = true;
  },
  methods: {
    scheduleFit() {
      if (this._fitFrame) cancelAnimationFrame(this._fitFrame);
      this._fitFrame = requestAnimationFrame(() => {
        this._fitFrame = null;
        this.fitToWidth();
      });
    },
    // Lay every day column out at its authored size with words kept whole, and
    // read how far its content overflows: the largest shrink that brings the
    // widest word inside its column is the one applied to all of them, so the
    // days keep matching sizes. All in one frame, before anything is painted.
    async fitToWidth() {
      const board = this.$refs.board;
      if (this._unmounted || !board) return;
      if (this.layoutMode === 'ticker') {
        if (this.fitZoom !== 1) this.fitZoom = 1;
        return;
      }
      this.measuring = true;
      await this.$nextTick();
      let ratio = 1;
      board.querySelectorAll('.ws-board__day').forEach((day) => {
        const inner = day.querySelector('.ws-board__day-inner');
        if (!inner) return;
        const available = day.clientWidth;
        const needed = inner.scrollWidth;
        if (available > 0 && needed > available + 1) ratio = Math.min(ratio, available / needed);
      });
      this.measuring = false;
      const next = ratio >= 1 ? 1 : Math.max(MIN_FIT_ZOOM, Math.floor(ratio * 100) / 100);
      if (next !== this.fitZoom) this.fitZoom = next;
    },
    dayStyle(index) {
      if (this.layoutMode !== 'week') return {};
      return getCenteredCardPlacement(this.weekColumnCount, index, this.dayBlocks.length, this.dayGap);
    },
    eventClasses(event) {
      return {
        'is-now': event.isNow,
        'is-next': event.isNext,
        'is-past': event.isPast,
      };
    },
    // A tint laid over the card's own background rather than replacing it, so
    // a translucent or patterned card keeps its look under the highlight.
    tint(alpha) {
      const color = scheduleColorWithAlpha(this.highlight, alpha);
      return color ? `linear-gradient(${color}, ${color})` : '';
    },
    cardStyle(event, index, total) {
      const style = { ...this.cardBase, borderRadius: `${CARD_RADIUS}px` };
      if (this.layoutMode === 'day') {
        Object.assign(style, getCenteredCardPlacement(this.columnCount, index, total, this.spacing));
      }
      if (this.highlight && event.isNow) {
        style.borderColor = this.highlight;
        style.backgroundImage = this.tint(0.12);
        style.boxShadow = `0 0 0 2px ${scheduleColorWithAlpha(this.highlight, 0.35) || this.highlight}`;
      } else if (this.highlight && event.isNext) {
        style.borderColor = this.highlight;
        style.borderStyle = 'dashed';
      }
      return style;
    },
    // Rows run down each column before the next column starts, so the day
    // still reads in time order with more than one column.
    rowsStyle(total) {
      if (this.layoutMode === 'ticker') return {};
      const columns = this.columnCount;
      const rows = Math.max(1, Math.ceil(total / columns));
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, auto)`,
        gridAutoFlow: 'column',
        columnGap: `${Math.max(this.spacing * 2, 16)}px`,
      };
    },
    rowStyle(event, index, total) {
      const half = Math.max(Math.round(this.spacing / 2), 4);
      const style = { padding: `${half}px 10px`, borderRadius: '8px' };
      if (this.layoutMode === 'ticker') {
        if (index > 0) style.borderLeft = `1px solid ${this.d.dividerColor}`;
      } else {
        const rows = Math.max(1, Math.ceil(total / this.columnCount));
        if (index % rows !== 0) style.borderTop = `1px solid ${this.d.dividerColor}`;
        // A divider drawn between rows would be lost under the rounded corner
        // of a highlighted row; square the edge it sits on instead.
        style.borderTopLeftRadius = index % rows === 0 ? '8px' : '0';
        style.borderTopRightRadius = index % rows === 0 ? '8px' : '0';
      }
      if (this.highlight && event.isNow) {
        style.backgroundImage = this.tint(0.14);
        style.boxShadow = `inset 4px 0 0 ${this.highlight}`;
      } else if (this.highlight && event.isNext) {
        style.boxShadow = `inset 4px 0 0 ${scheduleColorWithAlpha(this.highlight, 0.45) || this.highlight}`;
      }
      return style;
    },
  },
};
</script>

<style scoped>
.ws-board {
  box-sizing: border-box;
  width: 100%;
  padding: 16px;
}

.ws-board__day,
.ws-board__day-inner {
  min-width: 0;
}

.ws-board__heading {
  min-width: 0;
}

.ws-board__card,
.ws-board__row {
  min-width: 0;
}

.ws-board__rows {
  min-width: 0;
}

.ws-board__card.is-past,
.ws-board__row.is-past {
  opacity: 0.45;
}

.ws-board__empty {
  color: #6b7280;
  font-family: var(--font-sans, 'Poppins', sans-serif);
  font-size: 18px;
  line-height: 1.5;
}

/* --------------------------------- ticker ---------------------------------
   A horizontal artboard scrolls its content sideways as one line, so the board
   becomes a row: each day's heading, then its events, side by side. Each card
   is as wide as its own longest line. */
.ws-board--ticker {
  width: auto;
}

.ws-board--ticker .ws-board__days,
.ws-board--ticker .ws-board__day-inner,
.ws-board--ticker .ws-board__cards,
.ws-board--ticker .ws-board__rows {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
}

.ws-board--ticker .ws-board__days {
  gap: 48px;
}

.ws-board--ticker .ws-board__day-inner {
  gap: 24px;
}

.ws-board--ticker .ws-board__heading,
.ws-board--ticker .ws-board__card,
.ws-board--ticker .ws-board__row {
  flex: 0 0 auto;
  width: max-content;
  max-width: none;
}
</style>
